<script>
import baseMixin from '@/mixins/baseMixin'
import bookingFiltersMixin from '@/mixins/bookingFiltersMixin'
import dayjs from '@/services/date/index'
import CustomTooltip from '@/components/booking/bookingList/custom-tooltip.vue'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { Conditions } from '@/shared/constants/datePicker/picker'
import DatePicker from './date-picker.vue'

export default {
  name: 'DateFilter',
  components: {
    CustomTooltip,
    DatePicker,
  },
  mixins: [baseMixin, bookingFiltersMixin],
  emits: ['toggle', 'filter-dates'],
  data() {
    return {
      showDateFilter: false,
      dateApplied: this.$t('topFilterBar.dateFilter.title'),
    }
  },
  mounted() {
    this.updateDateApplied()
    document.addEventListener('click', this.handleClickOutsideDateFilter, true)
  },
  beforeUnmount() {
    document.removeEventListener(
      'click',
      this.handleClickOutsideDateFilter,
      true
    )
  },
  methods: {
    handleClickOutsideDateFilter(e) {
      const refsToCheck = ['dateFilterContainer']

      const path =
        typeof e.composedPath === 'function'
          ? e.composedPath()
          : (function fallbackPath(n) {
              const p = []
              while (n) {
                p.push(n)
                n = n.parentNode || n.host
              }
              p.push(window)
              return p
            })(e.target)

      const clickedInsideRef = refsToCheck.some((ref) => {
        const el = this.$refs[ref]
        return el && path.includes(el)
      })

      const clickedOnTrigger = path.some((node) => {
        if (!(node instanceof HTMLElement)) return false
        return node.closest('.date-filter')
      })

      const inOverlay = path.some(
        (node) =>
          node instanceof HTMLElement &&
          (node.closest('.v-menu__content') ||
            node.closest('.v-overlay__content') ||
            node.closest('.v-picker'))
      )

      const inCustomDatepicker = path.some(
        (node) =>
          node instanceof HTMLElement &&
          (node.classList.contains('custom-calendar') ||
            node.classList.contains('datepicker'))
      )

      if (
        !clickedInsideRef &&
        !clickedOnTrigger &&
        !inOverlay &&
        !inCustomDatepicker
      ) {
        this.showDateFilter = false
      }
    },
    toggleAnyDateFilter() {
      this.showDateFilter = !this.showDateFilter
      this.$emit('toggle')
    },
    clearFields() {
      this.setDateFilter({ startDate: null, endDate: null })
    },
    confirmDateFilter(dates) {
      this.updateDateApplied()

      this.$emit('filter-dates', dates)
      this.showDateFilter = false
    },
    updateDateApplied() {
      const formatDateText = (d) =>
        d ? dayjs(d).format(DateFormatToken.ll) : null

      switch (this.filterCondition) {
        case Conditions.IS:
        case Conditions.BEFORE:
        case Conditions.AFTER:
          this.dateApplied = this.filterStartDate
            ? formatDateText(this.filterStartDate)
            : this.$t('topFilterBar.dateFilter.title')
          break
        case Conditions.BETWEEN:
          this.dateApplied =
            this.filterStartDate && this.filterEndDate
              ? `${formatDateText(this.filterStartDate)} - ${formatDateText(
                  this.filterEndDate
                )}`
              : this.$t('topFilterBar.dateFilter.title')
          break
        default:
          this.dateApplied = this.$t('topFilterBar.dateFilter.title')
          break
      }
      this.$emit('applied-dates', this.dateApplied)
    },
  },
}
</script>

<template>
  <div style="position: relative">
    <CustomTooltip
      position="bottom"
      class="date-filter"
      :class="showDateFilter ? 'active' : ''"
      :text="this.$t('bookingList.headerName.date')"
    >
      <div class="cursor-pointer" @click.stop="toggleAnyDateFilter">
        <v-icon class="me-2"> mdi-calendar-month-outline </v-icon>
        <span class="top-filter-title">{{ dateApplied }}</span>
      </div>
    </CustomTooltip>

    <!-- Custom Calendar Style -->
    <DatePicker
      v-if="showDateFilter"
      ref="dateFilterContainer"
      class="custom-calendar mx-auto"
      :condition="filterCondition"
      :start-date="filterStartDate"
      :end-date="filterEndDate"
      @input="
        ([start, end]) => {
          filterStartDate = start
          filterEndDate = end
        }
      "
      @update-condition="filterCondition = $event"
      @clear-fields="clearFields"
      @close-date-filter="showDateFilter = false"
      @confirm-date-filter="confirmDateFilter"
      @applied-dates="dateApplied = $event"
    />
  </div>
</template>
